import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import NotificationBanner from '../../components/ui/NotificationBanner';
import aboutHero from '../../assets/images/hero/about.jpg';
import vrPerson from '../../assets/images/home/vr_experience.jpg';
import storyImage from '../../assets/images/about/ai_learning.jpg';
import ceoImage from '../../assets/images/about/ceo.png';
import ctoImage from '../../assets/images/about/cto.png';
import partnershipImage from '../../assets/images/solutions/experience_video_cover.jpg';

const About = () => {
    return (
        <>
            <Helmet>
                <title>About Us - Nexgen Learning - AI-Driven Education</title>
                <meta name="description" content="Learn about NexGen Learning's mission to revolutionize education through AI and innovative technology." />
            </Helmet>
            <Header />
            <NotificationBanner />
            <main>
                {/* Hero Section with Background Image */}
                <section className="relative pt-40 pb-32">
                    {/* Background Image with Overlay */}
                    <div className="absolute inset-0">
                        <img
                            src={aboutHero}
                            alt="About Background"
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/50 to-gray-900/30"></div>
                    </div>

                    {/* Content */}
                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center">
                            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 mt-10">
                                Our Mission
                            </h1>
                            <p className="text-lg md:text-xl text-gray-200 max-w-3xl mx-auto">
                                Revolutionizing Education Through AI
                            </p>
                        </div>
                    </div>
                </section>

                {/* About Section */}
                <section className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                            {/* Left Side - Image */}
                            <div className="relative">
                                <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-purple-500/20 rounded-2xl transform rotate-3"></div>
                                <img
                                    src={vrPerson}
                                    alt="VR Person Learning"
                                    className="relative rounded-2xl shadow-xl w-full h-auto"
                                />
                            </div>
                            {/* Right Side - Description */}
                            <div className="space-y-6">
                                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                                    About
                                </h2>
                                <h3 className="text-2xl md:text-3xl font-semibold text-primary">
                                    Empowering Education Through Innovation and AI
                                </h3>
                                <p className="text-lg text-gray-600 leading-relaxed">
                                    At NexGen Learning, we're driven by a bold vision: to transform the world of education through innovative artificial intelligence. Our team is on a mission to make learning not just accessible, but engaging and empowering for every student. Through advanced AI-driven tools, we strive to bring the highest quality of personalized education to students everywhere, breaking down traditional barriers and creating a world where learning is as dynamic and diverse as the students themselves.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Story Section */}
                <section className="py-20 bg-gray-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                            {/* Left Side - Description */}
                            <div className="space-y-6">
                                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                                    Our Story
                                </h2>
                                <h3 className="text-2xl md:text-3xl font-semibold text-primary">
                                    From a Vision to a Global Movement in Education
                                </h3>
                                <p className="text-lg text-gray-600 leading-relaxed">
                                    Our journey began with a shared vision: to solve the educational challenges of today through innovative technology. NexGen Learning was founded by a group of passionate educators, data scientists, and technologists who recognized the potential of AI to enhance learning experiences. From our humble beginnings in a small office, we have grown into a dynamic team dedicated to pushing the boundaries of educational technology. Today, NexGen Learning is proud to serve thousands of students and educators around the world, providing tools that make learning more effective, enjoyable, and accessible.
                                </p>
                            </div>

                            {/* Right Side - Image */}
                            <div className="relative">
                                <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-purple-500/20 rounded-2xl transform -rotate-3"></div>
                                <img
                                    src={storyImage}
                                    alt="AI Learning Experience"
                                    className="relative rounded-2xl shadow-xl w-full h-auto"
                                />
                            </div>
                        </div>
                    </div>
                </section>

                {/* Team Section */}
                <section className="py-20 bg-gradient-to-b from-gray-900 to-gray-800">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                                Meet Our Leadership
                            </h2>
                            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
                                The visionaries behind NexGen Learning's innovative approach to education
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 justify-items-center">
                            {/* CEO Image */}
                            <div className="relative w-1/2">
                                <img
                                    src={ceoImage}
                                    alt="Dr. Sarah Chen - CEO"
                                    className="relative rounded-2xl shadow-xl w-full h-auto"
                                />
                            </div>

                            {/* CTO Image */}
                            <div className="relative w-1/2">
                                <img
                                    src={ctoImage}
                                    alt="Michael Rodriguez - CTO"
                                    className="relative rounded-2xl shadow-xl w-full h-auto"
                                />
                            </div>
                        </div>
                    </div>
                </section>

                {/* Partnerships Section */}
                <section className="py-20 bg-gray-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            {/* Left Side - Description */}
                            <div className="space-y-6">
                                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                                    Educational Partnerships
                                </h2>
                                <h3 className="text-xl text-primary font-semibold">
                                    Collaborating for Better Learning
                                </h3>
                                <p className="text-lg text-gray-600 leading-relaxed">
                                    NexGen Learning partners with leading educational institutions and innovative EdTech companies to deliver personalized, AI-driven learning experiences worldwide. We offer flexible solutions, including interactive courses and adaptive study tools, making education more accessible and engaging for every learner.
                                </p>
                                <div className="flex flex-wrap gap-4">
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                                        <span className="text-gray-700">Global Reach</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                                        <span className="text-gray-700">Flexible Solutions</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                                        <span className="text-gray-700">Innovative Tools</span>
                                    </div>
                                </div>
                            </div>

                            {/* Right Side - Image */}
                            <div className="relative">
                                <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-purple-500/20 rounded-2xl transform -rotate-3"></div>
                                <img
                                    src={partnershipImage}
                                    alt="Educational Partnerships"
                                    className="relative rounded-2xl shadow-xl w-full h-auto"
                                />
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
};

export default About;
